import { PlusIcon, TrashAltIcon } from "assets/icons";
import { If } from "components";
import { Controller, useFieldArray, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import {
  Checkbox,
  FormField,
  IconButton,
  Input,
  RangePicker,
  RangePickerValue
} from "ui";

import {
  MAX_QTY_RANGE_VALUE,
  MIN_QTY_RANGE_VALUE
} from "../../../../../constants";
import { createInitialBulkDiscounts } from "../../../../../helpers";
import { ProductFormValues } from "../../ProductDetailsForm.types";
import { VariantBlockPartProps } from "./VariantBlock.types";

import styles from "../../ProductDetailsForm.module.scss";

export const VariantBulkDiscountsBlock = ({
  variantIndex
}: VariantBlockPartProps) => {
  const { t } = useTranslation();
  const { control, setValue, watch } = useFormContext<ProductFormValues>();
  const bulkDiscountsWatch = watch(`variants.${variantIndex}.bulkDiscounts`);

  const {
    fields: bulkDiscounts,
    append: appendBulkDiscounts,
    remove: removeBulkDiscounts,
    update: updateBulkDiscount,
    insert: insertBulkDiscount
  } = useFieldArray({
    control,
    name: `variants.${variantIndex}.bulkDiscounts`
  });

  const handleRangePikerChange = (
    value: RangePickerValue,
    discountIndex: number
  ) => {
    const prevDiscount = bulkDiscountsWatch[discountIndex - 1];
    const nextDiscount = bulkDiscountsWatch[discountIndex + 1];

    if (prevDiscount) {
      updateBulkDiscount(discountIndex - 1, {
        ...prevDiscount,
        max: value.from - 1
      });
    }

    if (nextDiscount) {
      updateBulkDiscount(discountIndex + 1, {
        ...nextDiscount,
        min: value.to + 1
      });
    }

    updateBulkDiscount(discountIndex, {
      ...bulkDiscountsWatch[discountIndex],
      min: value.from,
      max: value.to
    });
  };

  const handleDeleteBulkDiscount = (discountIndex: number) => {
    if (bulkDiscounts[discountIndex + 1] && bulkDiscounts[discountIndex - 1]) {
      updateBulkDiscount(discountIndex + 1, {
        ...bulkDiscountsWatch[discountIndex + 1],
        min: bulkDiscountsWatch[discountIndex].min
      });
    }

    removeBulkDiscounts(discountIndex);
  };

  const handleAddBulkDiscount = () => {
    insertBulkDiscount(1, {
      min: bulkDiscountsWatch[0].max + 1,
      max: bulkDiscountsWatch[0].max + 5,
      discount: null
    });

    updateBulkDiscount(2, {
      min: bulkDiscountsWatch[0].max + 6,
      max: bulkDiscountsWatch[1].max,
      discount: bulkDiscountsWatch[1].discount
    });
  };

  return (
    <>
      <Controller
        control={control}
        name={`variants.${variantIndex}.discountAmount`}
        render={({ field: { onChange: onDiscountChange } }) => (
          <Checkbox
            className={styles.productForm__bulkDiscountCheckbox}
            label={t("Add discounts")}
            checked={!!bulkDiscounts.length}
            onChange={(e) => {
              if (e.target.checked) {
                onDiscountChange(null);
                setValue(
                  `variants.${variantIndex}.activeCommissionType`,
                  "fixed"
                );
                setValue(`variants.${variantIndex}.commissionPercentage`, null);
                appendBulkDiscounts(createInitialBulkDiscounts());
              } else {
                removeBulkDiscounts();
              }
            }}
          />
        )}
      />

      {bulkDiscounts.map((bulkDiscount, discountIndex) => (
        <Controller
          control={control}
          key={bulkDiscount.id}
          name={`variants.${variantIndex}.bulkDiscounts.${discountIndex}.discount`}
          render={({ fieldState: { error } }) => (
            <FormField errorMessage={error?.message}>
              <div
                key={bulkDiscount.id}
                className={styles.productForm__bulkDiscountRow}
              >
                <div>
                  <FormField
                    key={bulkDiscount.id}
                    label={discountIndex === 0 ? t("Qty range") : undefined}
                  >
                    <RangePicker
                      value={{
                        from: bulkDiscount.min,
                        to: bulkDiscount.max
                      }}
                      onChange={(value) =>
                        handleRangePikerChange(value, discountIndex)
                      }
                      min={
                        discountIndex === 0
                          ? MIN_QTY_RANGE_VALUE
                          : bulkDiscounts[discountIndex - 1].max + 1
                      }
                      max={
                        discountIndex === bulkDiscounts.length - 1
                          ? MAX_QTY_RANGE_VALUE
                          : bulkDiscounts[discountIndex + 1].max - 1
                      }
                      width={160}
                      disabled={discountIndex === bulkDiscounts.length - 1}
                    />
                  </FormField>
                </div>

                <div>
                  <Controller
                    control={control}
                    name={`variants.${variantIndex}.bulkDiscounts.${discountIndex}.discount`}
                    render={({ field: { value, onChange } }) => (
                      <Input
                        key={bulkDiscount.id}
                        label={discountIndex === 0 ? t("Discount") : undefined}
                        value={value ?? ""}
                        onChange={(e) => onChange(e.target.value ?? null)}
                        type="number"
                        onlyInteger={true}
                        placeholder={t("Enter Discount")}
                        maxLength={2}
                      />
                    )}
                  />
                </div>

                <If condition={discountIndex !== bulkDiscounts.length - 1}>
                  <div
                    className={
                      styles.productForm__bulkDiscountRow__btnContainer
                    }
                  >
                    {bulkDiscounts.length > 2 ? (
                      <IconButton
                        icon={
                          <TrashAltIcon
                            fill={"#FF0033"}
                            width={24}
                            height={24}
                          />
                        }
                        onClick={() => handleDeleteBulkDiscount(discountIndex)}
                      />
                    ) : (
                      <IconButton
                        className={
                          styles.productForm__bulkDiscountRow__btnContainer__plusBtn
                        }
                        icon={
                          <PlusIcon
                            color={"#FFF"}
                            onClick={handleAddBulkDiscount}
                          />
                        }
                      />
                    )}
                  </div>
                </If>
              </div>
            </FormField>
          )}
        />
      ))}
    </>
  );
};
